import { useCallback, useEffect, useRef, useState } from 'react';
import ErrorReporter from 'src/monitoring/errorReporter';
import type { RealtimeConnectionOptions } from '../types/realtime-client.type';
import type { RealtimeConnectionStatus } from '../types/realtime.types';
import {
  getSocketUrlEnv,
  buildSocketUrl,
  isSocketOpen,
} from '../utils/socket-connection.util';

const SOCKET_URL = getSocketUrlEnv();
const PING_INTERVAL_MS = 4 * 60 * 1000;
const RECONNECT_BASE_DELAY_MS = 2000;
const RECONNECT_MAX_DELAY_MS = 30000;

/**
 * Owns transport generations, authentication, heartbeat and reconnect cleanup.
 * @param options - Authentication and subscription/message lifecycle callbacks.
 * @returns Current transport status.
 */
export const useRealtimeConnection = ({
  socketRef,
  isAuthenticated,
  isLoading,
  getAccessTokenSilently,
  handleSocketMessage,
  resubscribeCurrentTopics,
  resetAuthorization,
}: RealtimeConnectionOptions): RealtimeConnectionStatus => {
  const [status, setStatus] = useState<RealtimeConnectionStatus>('disabled');
  const reconnectTimeoutRef = useRef<number | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isAuthenticated || isLoading) {
      setStatus('disabled');
      return;
    }

    if (!SOCKET_URL) {
      ErrorReporter.sendMessage(
        'VITE_SOCKET_CONNECTION_URL is not configured; realtime disabled.',
        'warning'
      );
      setStatus('disabled');
      return;
    }

    shouldReconnectRef.current = true;
    let disposed = false;
    const isDisposed = () => disposed;

    const startPing = () => {
      pingIntervalRef.current = window.setInterval(() => {
        const socket = socketRef.current;
        if (!socket || !isSocketOpen(socket)) {
          return;
        }

        socket.send(JSON.stringify({ type: 'ping' }));
      }, PING_INTERVAL_MS);
    };

    const scheduleReconnect = () => {
      if (disposed || !shouldReconnectRef.current) {
        return;
      }

      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * attempt,
        RECONNECT_MAX_DELAY_MS
      );

      setStatus('reconnecting');
      reconnectTimeoutRef.current = window.setTimeout(() => {
        void connect();
      }, delay);
    };

    const connect = async () => {
      if (disposed || !shouldReconnectRef.current) {
        return;
      }

      setStatus(
        reconnectAttemptRef.current > 0 ? 'reconnecting' : 'connecting'
      );

      let token = '';
      try {
        token = await getAccessTokenSilently();
      } catch (error) {
        ErrorReporter.sendException(error);
        scheduleReconnect();
        return;
      }

      if (isDisposed()) return;

      let socketUrl: string;
      try {
        socketUrl = buildSocketUrl(SOCKET_URL, token);
      } catch (error) {
        ErrorReporter.sendException(error);
        setStatus('disabled');
        return;
      }

      resetAuthorization();
      const socket = new WebSocket(socketUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed || socketRef.current !== socket) return;
        reconnectAttemptRef.current = 0;
        clearTimers();
        setStatus('connected');
        startPing();
        resubscribeCurrentTopics();
      };

      socket.onmessage = (event) => {
        if (!disposed && socketRef.current === socket) {
          handleSocketMessage(event);
        }
      };

      socket.onclose = () => {
        if (disposed || socketRef.current !== socket) return;
        clearTimers();
        socketRef.current = null;
        resetAuthorization();
        setStatus('offline');
        if (shouldReconnectRef.current) {
          scheduleReconnect();
        }
      };

      socket.onerror = () => {
        socket.close();
      };
    };

    void connect();

    return () => {
      disposed = true;
      shouldReconnectRef.current = false;
      resetAuthorization();
      clearTimers();
      socketRef.current?.close();
      socketRef.current = null;
      setStatus('disabled');
    };
  }, [
    clearTimers,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    handleSocketMessage,
    resubscribeCurrentTopics,
    resetAuthorization,
    socketRef,
  ]);

  return status;
};
